import type { LinksFunction } from 'react-router'
import siteStylesheetUrl from '#app/styles/site.css?url'

/** `/?apply-link-needed=1` opens the "application link needed" popup. */
export const APPLY_LINK_NEEDED_PARAM = 'apply-link-needed'

/** The two audiences the public website speaks to. */
export type Audience = 'tenant' | 'landlord'
export const AUDIENCES = [
	'tenant',
	'landlord',
] as const satisfies readonly Audience[]

export function isAudience(value: unknown): value is Audience {
	return value === 'tenant' || value === 'landlord'
}

/**
 * Every destination the website links to, in one place (the reference called
 * this TT_LINKS). Landlord CTAs book a demo; tenant CTAs start an application,
 * which is why the tenant signup carries a redirect into the screening flow.
 */
export const SITE_LINKS = {
	demo: 'https://calendly.com/truetenant',
	home: { tenant: '/', landlord: '/landlord' },
	signIn: { tenant: '/tenant/login', landlord: '/landlord/login' },
	getStarted: {
		// Signed out goes to login; the screening page then resumes the
		// tenant's application, or tells them to use their landlord's link.
		tenant: '/tenant/login?redirectTo=%2Ftenant%2Fscreening',
		landlord: '/landlord/signup',
	},
	/** The in-page anchor "How it Works" scrolls to: the timeline track. */
	howItWorks: '#how',
	terms: '/terms',
	privacy: '/privacy',
	cookies: '/cookies',
	/** Signed-in destinations for the header. */
	tenantApp: '/tenant/screening',
	landlordDashboard: '/dashboard',
	logout: '/logout',
} as const

/**
 * The href a page call to action should use for this viewer. The signup
 * pages send anyone already signed in back to the home page, so for a signed
 * in tenant or landlord the tenant "Get started" goes straight into the
 * screening flow, where the header's Get started takes a signed-in tenant.
 * Every other link (Book a demo) is returned unchanged.
 */
export function ctaHrefFor(href: string, signedIn: boolean): string {
	return signedIn && href === SITE_LINKS.getStarted.tenant
		? SITE_LINKS.tenantApp
		: href
}

export function isExternalHref(href: string) {
	return /^https?:\/\//.test(href)
}

export const HERO_IMAGE = {
	src: '/img/site/hero-toronto-1600.webp',
	srcSet:
		'/img/site/hero-toronto-1600.webp 1600w, /img/site/hero-toronto-2560.webp 2560w, /img/site/hero-toronto-3840.webp 3840w',
	// The photo covers a 100vh stage at up to 1.1x scale (hero-motion.ts),
	// so on a viewport taller than the photo's 5:4 it is drawn 1.25 x the
	// height wide, not 100vw. '100vw' made phones pick the 1600 file and
	// stretch it about 2x (TT-157: pixelated hero on mobile).
	sizes: '(max-aspect-ratio: 5/4) 138vh, 110vw',
	alt: 'Toronto skyline and the CN Tower rising through morning fog',
} as const

export const AUTH_IMAGE = {
	tenant: '/img/site/auth-tenant.webp',
	landlord: '/img/site/auth-landlord.webp',
} as const

/**
 * `links` for every website route: the scoped stylesheet.
 *
 * The hero photo needs no explicit preload. `PhotoHero` renders it as an
 * `<img fetchPriority="high">` with its srcset, and React 19 already hoists
 * a matching `<link rel="preload" as="image">` into the head for that; a
 * second one here only duplicated it. (Chromium also treats the full-bleed
 * hero as a background image, so it is not the LCP element either way.)
 */
export function siteLinks(): LinksFunction {
	return () => [{ rel: 'stylesheet', href: siteStylesheetUrl }]
}
